import config from '@config';
import { brandsOpenApi, INITIAL_CCC_COMMISSION, INITIAL_NCC_COMMISSION, INITIAL_ROS_COMMISSION } from '@constants';
import {
    ACCOUNT_RESOURCE_STATUS,
    ACCOUNT_STATUS,
    ACCOUNT_USER_ROLE,
    ADDRESS_TYPE,
    CAMPAIGN_STATUS,
    CONNECTOR_KLICKLY_STATUS,
    CONTACT_TYPE,
    CONTACT_VALUE_TYPE,
    CONTENT_TYPE,
    DOMAIN_CLASS,
    PLATFORM_ID,
    PLATFORM_NAME_BY_PLATFORM_ID,
    PURCHASE_EVENT_STATUS,
    SCRIPT_TAG_STATUS,
    SCRIPT_TAG_STATUS_TITLE,
    TOKEN_STATUS
} from '@enums';
import { axiosBoa } from '@utils';
import dayjs from 'dayjs';
import _ from 'lodash';
import { getParent, types } from 'mobx-state-tree';
import { isHistoricalSetupPending } from './utils/historicalSetup';
import {
    Address,
    Contact,
    CurrentAccountModel,
    SetupInfo,
    State,
    TeamMember,
    UnsubscribeList,
    Verification
} from './constants';
import { createStoreAction, mapPaymentMethod } from './utils';

export const AccountsStore = types
    .model('AccountsStore', {
        currentAccount: CurrentAccountModel,
        // * setup info
        setupInfoPending: types.maybeNull(types.boolean),
        accountStatusPending: types.maybeNull(types.boolean),
        setupInfo: types.maybeNull(SetupInfo),
        // * states
        states: types.array(State),
        // * team members
        teamMembers: types.maybe(types.frozen(types.array(TeamMember))),
        // * contacts, addresses
        contactsForCustomers: types.array(Contact),
        addressForCustomers: types.maybe(Address),
        contactsForKlickly: types.array(Contact),
        addressForKlickly: types.maybe(Address),
        notificationContacts: types.array(Contact),
        // * others
        unsubscribeList: types.array(UnsubscribeList),
        verification: types.maybeNull(Verification),
        pending: true,
        hasLaunchedCampaign: false,
        inviteCompletePending: false,
        purchaseSetupPending: false,
        historicalSetupOrdersPending: false,
        historicalSetupCustomersPending: false,
        historicalSetupStartedAt: types.maybeNull(types.number),
        scriptTagSetupPending: types.maybeNull(types.boolean)
    })
    .views((self) => ({
        get root() {
            return getParent(self);
        },
        get platformName() {
            return PLATFORM_NAME_BY_PLATFORM_ID[self.currentAccount?.platformId];
        },
        get timeZone() {
            return self.currentAccount.settings?.timeZoneDashboard ?? self.currentAccount.timeZone;
        },
        get isShopify() {
            return self.currentAccount?.platformId === PLATFORM_ID.SHOPIFY;
        },
        get isBigCommerce() {
            return self.currentAccount?.platformId === PLATFORM_ID.BIGCOMMERCE;
        },
        get isManual() {
            return self.currentAccount?.platformId === PLATFORM_ID.KLICKLY;
        },
        get isManualStoreWithNoCampaigns() {
            return self.isManual && !self.currentAccount?.hasCampaign;
        },
        get isUpdateCsv() {
            if (!self.isManual || !self.currentAccount?.productSetupSettings) {
                return false;
            }

            const productSetupSettings = self.currentAccount.productSetupSettings;
            return (
                productSetupSettings.csv &&
                !productSetupSettings.api &&
                !productSetupSettings.googleFeed &&
                !productSetupSettings.metaFeed &&
                !productSetupSettings.webhooks
            );
        },
        get productsSyncProgress() {
            return self?.currentAccount?.syncProgress.find((progress) => progress.name === 'products');
        },
        get hasProducts() {
            return self?.currentAccount?.syncProgress.find((progress) => progress.name === 'products')?.totalCount > 0;
        },
        get isManualWithSyncInProgress() {
            if (!self.isManual) return false;
            const productsProgress = self.productsSyncProgress;
            return productsProgress?.status === ACCOUNT_RESOURCE_STATUS.IN_PROGRESS;
        },
        get isManualWithSyncFailed() {
            if (!self.isManual) return false;
            const productsProgress = self.productsSyncProgress;
            return productsProgress?.status === ACCOUNT_RESOURCE_STATUS.FAILED;
        },
        get isManualWithSyncInProgressOrFailed() {
            const isSyncInProgress = self.isManualWithSyncInProgress;
            const isSyncFailed = self.isManualWithSyncFailed;
            return isSyncInProgress || isSyncFailed;
        },
        get logoUrl() {
            return self?.currentAccount?.logoUrl;
        },
        get stripeAccountStatus() {
            return self?.currentAccount?.stripeSettings?.stripeAccountStatus;
        },
        get companyName() {
            return self?.currentAccount?.companyName;
        },
        get companyInitials() {
            if (!self.companyName) {
                return '';
            }
            const words = self.companyName.split(' ');
            let initials = '';
            if (words.length >= 2) {
                initials = words[0].charAt(0).toUpperCase() + words[1].charAt(0).toUpperCase();
            } else if (words.length === 1) {
                initials = words[0].charAt(0).toUpperCase();
            }
            return initials;
        },
        get isPaused() {
            return self.currentAccount?.status === ACCOUNT_STATUS.PAUSED;
        },
        get isActive() {
            return self.currentAccount?.status === ACCOUNT_STATUS.ACTIVE;
        },
        get isShutDown() {
            return self.currentAccount?.status === ACCOUNT_STATUS.SHUTDOWN;
        },
        get isPaymentOnHold() {
            return self.currentAccount?.stripeSettings?.holdPayments;
        },
        get showPaymentFailedModal() {
            return self.currentAccount?.settings?.showModal?.paymentFailed;
        },
        get apiToken() {
            return self.setupInfo?.apiTokens?.find((token) => token.status === TOKEN_STATUS.ACTIVE)?.token || '';
        },
        get isUninstalled() {
            return self.currentAccount?.status === ACCOUNT_STATUS.DISCONNECTED;
        },
        get isBlocked() {
            return self.currentAccount?.status === ACCOUNT_STATUS.BLOCKED;
        },
        get isConnectorConnected() {
            return self.currentAccount?.connectorStatus === CONNECTOR_KLICKLY_STATUS.CONNECTED;
        },
        get hasAccounts() {
            return true;
        },
        get hasPaymentMethod() {
            return !!self.setupInfo?.paymentMethod;
        },
        get hasGUID() {
            return !!self.currentAccount?.thirdPartyAppsSettings?.transUnionGUID;
        },
        get hasGUIDRequest() {
            return !!self.currentAccount?.thirdPartyAppsSettings?.transUnionRequestMessage;
        },
        get commissionsApplied() {
            const commission = self.currentAccount?.commission;
            if (!commission) return false;
            return (
                commission.restOfSite !== INITIAL_ROS_COMMISSION ||
                commission.newCustomer !== INITIAL_NCC_COMMISSION ||
                commission.coldCustomer !== INITIAL_CCC_COMMISSION
            );
        },
        get manualStoreSetupFinished() {
            return (
                self.setupInfo.scriptTag?.status === SCRIPT_TAG_STATUS.CONNECTED &&
                self.setupInfo.purchaseEventStatus === PURCHASE_EVENT_STATUS.CONNECTED &&
                self.setupInfo.productCatalog > 0
            );
        },
        get scriptTagIntegrationStatusTitle() {
            const connected = self.currentAccount?.domainScripts?.every(
                ({ status }) => status === SCRIPT_TAG_STATUS.CONNECTED
            );
            if (connected) {
                return SCRIPT_TAG_STATUS_TITLE.CONNECTED;
            }
            const pending = self.currentAccount?.domainScripts?.some(
                ({ status }) => status === SCRIPT_TAG_STATUS.PENDING
            );
            const hasDspQueryParams = self.currentAccount?.domainScripts.every(
                ({ templateVariables }) => !!templateVariables.searchParams.b_type_visit
            );
            if (pending || !hasDspQueryParams) {
                return SCRIPT_TAG_STATUS_TITLE.PENDING;
            }
            return SCRIPT_TAG_STATUS_TITLE.NOT_CONNECTED;
        },
        get paymentMethod() {
            return self.setupInfo?.paymentMethod;
        },
        getInitValuesUnsubscribe() {
            return self.unsubscribeList.reduce((initialValues, account) => {
                initialValues[account.contactId] = account.notifications;
                return initialValues;
            }, {});
        },
        get showSetupPayoutsModal() {
            return (
                self.currentAccount?.settings?.showModal?.setupPayouts &&
                !self.currentAccount.stripeSettings.stripeAccountId
            );
        },
        get showCreateCampaignModal() {
            return self.currentAccount?.settings?.showModal?.createCampaign;
        },
        get commissionOffsetEnabled() {
            return self.currentAccount?.commission?.commissionOffsetEnabled;
        },
        get registerDate() {
            return dayjs(self.currentAccount?.createdAt).format('MMM DD, YYYY');
        },
        get scriptTag() {
            return self.setupInfo?.scriptTag?.src;
        },
        get shopDomain() {
            return self.currentAccount?.domains?.[0]?.shopDomain;
        },
        get customShopDomain() {
            return self.currentAccount?.domains.find((domain) => domain.class === DOMAIN_CLASS.MAIN_CUSTOM)?.shopDomain;
        },
        get mainEcommerceShopDomain() {
            return self.currentAccount?.domains.find((domain) => domain.class === DOMAIN_CLASS.MAIN_ECOMMERCE)
                ?.shopDomain;
        },
        get primaryShopDomain() {
            return self.currentAccount?.domains.find((domain) => domain.isPrimary === true)?.shopDomain;
        },
        get domainsUrls() {
            return self.currentAccount.domains.map((domain) => domain.shopDomain);
        },
        get settingsLink() {
            if (self.currentAccount?.platformId === PLATFORM_ID.SHOPIFY) {
                const shopDomain = self.mainEcommerceShopDomain;
                return `https://admin.shopify.com/store/${shopDomain?.replace(/\.myshopify\.com$/, '')}/admin/settings`;
            } else if (self.currentAccount?.platformId === PLATFORM_ID.BIGCOMMERCE) {
                return `https://store-${self.currentAccount.externalId}.mybigcommerce.com/manage/settings-list`;
            } else {
                return '';
            }
        },
        get shopifySettingsLink() {
            const shopDomain = self.mainEcommerceShopDomain;
            return `https://admin.shopify.com/store/${shopDomain?.replace(/\.myshopify\.com$/, '')}`;
        },
        get bigcommerceSettingsLink() {
            const shopDomain = self.mainEcommerceShopDomain;
            return `https://${shopDomain}`;
        },
        // * contacts, addresses
        get klicklyEmail() {
            return self.contactsForKlickly?.find((contact) => contact.valueType === CONTACT_VALUE_TYPE.EMAIL);
        },
        get klicklyPhone() {
            return self.contactsForKlickly?.find((contact) => contact.valueType === CONTACT_VALUE_TYPE.PHONE);
        },
        get storeOwner() {
            return self.teamMembers?.find((member) => member.role === ACCOUNT_USER_ROLE.OWNER);
        },
        get staffAccounts() {
            return self.teamMembers?.filter((member) => member.role !== ACCOUNT_USER_ROLE.OWNER);
        },
        get customersEmail() {
            return self.contactsForCustomers?.find((contact) => contact.valueType === CONTACT_VALUE_TYPE.EMAIL);
        },
        get customersPhone() {
            return self.contactsForCustomers?.find((contact) => contact.valueType === CONTACT_VALUE_TYPE.PHONE);
        },
        get isCustomerInfoValid() {
            return !!self.customersEmail?.value && !!self.customersPhone?.value;
        },
        get isKlicklyInfoValid() {
            return !!self.klicklyEmail?.value && !!self.klicklyPhone?.value && !!self.addressForKlickly?.confirmed;
        },
        get trimmedDomain() {
            const shopDomain = self.mainEcommerceShopDomain;
            return shopDomain ? shopDomain.replace(/\.myshopify\.com$/, '') : '';
        },
        get customCreativesEnabled() {
            return self.currentAccount?.settings?.customCreativesEnabled;
        },
        get shopifyCrossCheckGuideVisible() {
            return self.isShopify && !!self.currentAccount?.shopifyCrossCheckGuide;
        },
        get ga4PropertyIdSectionVisible() {
            return self.currentAccount?.ga4Settings?.propertyIdSectionVisible ?? false;
        }
    }))
    .actions((self) => {
        const action = createStoreAction(self);

        return {
            // * Account
            getCurrentAccount: action(function* getCurrentAccount() {
                const account = yield axiosBoa.get(brandsOpenApi.accounts.getCurrent, {
                    params: {
                        include:
                            'domains,domain_scripts,third_party_apps_settings,account_resources,klickly_api_tokens,commission,stripe_settings,settings,product_setup_settings'
                    }
                });
                // just rename
                account.syncProgress = account.accountResources;
                delete account.accountResources;
                if (self.currentAccount) {
                    Object.assign(self.currentAccount, account);
                } else {
                    self.currentAccount = account;
                }
            }),
            updateAccount: action(function* updateAccount(data, type = CONTENT_TYPE.FORM) {
                const contentType = {
                    json: 'application/json',
                    form: 'multipart/form-data'
                };

                const account = yield axiosBoa.patch(brandsOpenApi.accounts.update, data, {
                    headers: {
                        'Content-Type': contentType[type]
                    }
                });
                Object.assign(self.currentAccount, account);
                self.root.authStore.updateAccount(self.currentAccount.id, account);
            }),
            // * syncProgress
            getAccountSyncProgress: action(function* getAccountSyncProgress() {
                const response = yield axiosBoa.get(brandsOpenApi.accounts.getCurrent, {
                    params: { select: 'account_resources' }
                });
                response.accountResources.forEach((newItem) => {
                    const existingIndex = _.findIndex(self.currentAccount.syncProgress, { id: newItem.id });
                    if (existingIndex !== -1) {
                        _.assign(self.currentAccount.syncProgress[existingIndex], newItem);
                    } else {
                        self.currentAccount.syncProgress.push(newItem);
                    }
                });
            }),
            // * Addresses
            getAddressForCustomers: action(function* getAddressForCustomers() {
                const response = yield axiosBoa.get(brandsOpenApi.addresses.list, {
                    params: { type: [ADDRESS_TYPE.FOR_CUSTOMER] }
                });
                self.addressForCustomers = response.data[0];
            }),
            getAddressForKlickly: action(function* getAddressForKlickly() {
                const response = yield axiosBoa.get(brandsOpenApi.addresses.list, {
                    params: { type: [ADDRESS_TYPE.FOR_KLICKLY] }
                });
                self.addressForKlickly = response.data[0];
            }),
            // * Contacts
            getContactsForCustomers: action(function* getContactsForCustomers() {
                const response = yield axiosBoa.get(brandsOpenApi.contacts.list, {
                    params: { type: [CONTACT_TYPE.FOR_CUSTOMER] }
                });
                self.contactsForCustomers = response.data;
            }),
            getContactsForKlickly: action(function* getContactsForCustomers() {
                const response = yield axiosBoa.get(brandsOpenApi.contacts.list, {
                    params: { type: [CONTACT_TYPE.FOR_KLICKLY] }
                });
                self.contactsForKlickly = response.data;
            }),
            getNotificationContacts: action(function* getNotificationContacts() {
                const response = yield axiosBoa.get(brandsOpenApi.contacts.list, {
                    params: { limit: 50, type: CONTACT_TYPE.NOTIFICATION }
                });
                self.notificationContacts = response.data.map((contact) => ({
                    ...contact,
                    value:
                        contact.valueType === CONTACT_VALUE_TYPE.PHONE ? contact.value.replace('+', '') : contact.value
                }));
            }),
            getUnsubscribeList: action(function* unsubscribe(token) {
                self.unsubscribeList = yield axiosBoa.get(brandsOpenApi.notifications.unsubscribeList(token));
            }),
            updateContacts: action(function* updateContacts(body) {
                yield axiosBoa.patch(brandsOpenApi.notifications.unsubscribe, body);
            }),
            resetNotificationContacts: () => {
                self.notificationContacts = [];
            },
            //  * Campaigns
            checkLaunchedCampaign: action(function* checkLaunchedCampaign() {
                const response = yield axiosBoa.get(brandsOpenApi.campaigns.list, {
                    params: { statuses: [CAMPAIGN_STATUS.ACTIVE], limit: 1 }
                });
                self.hasLaunchedCampaign = !!response.data.length;
            }),
            // * Commissions
            getAccountCommissions: action(function* getAccountCommissions() {
                const response = yield axiosBoa.get(brandsOpenApi.accounts.getCurrent, {
                    params: { select: 'commission' }
                });
                self.currentAccount.commission = response.commission;
            }),
            updateCommissions: action(function* updateCommissions(commission) {
                const response = yield axiosBoa.patch(brandsOpenApi.accounts.update, { commission });
                Object.assign(self.currentAccount.commission, response.commission);
            }),
            // * productTypes
            getAccountProductTypes: action(function* getAccountProductTypes() {
                const response = yield axiosBoa.get(brandsOpenApi.accounts.getCurrent, {
                    params: { select: 'product_types' }
                });
                self.currentAccount.productTypes = response.productTypes.filter(({ title }) => Boolean(title));
            }),
            resetProductTypes: () => {
                self.currentAccount.productTypes = [];
            },
            // * collections
            getAccountCollections: action(function* getAccountCollections() {
                const response = yield axiosBoa.get(brandsOpenApi.accounts.getCurrent, {
                    params: { select: 'product_collections' }
                });
                self.currentAccount.productCollections = response.productCollections;
            }),
            resetProductCollections: () => {
                self.currentAccount.productCollections = [];
            },
            // * bestSellers
            getBestSellersCount: action(function* getBestSellersCount() {
                const response = yield axiosBoa.get(brandsOpenApi.accounts.getCurrent, {
                    params: { select: 'best_sellers' }
                });
                // stays null when the API does not know this field yet - see useTypeAvailability
                self.currentAccount.bestSellersCount = response.bestSellersCount ?? null;
            }),
            // * Settings
            getAccountSettings: action(function* getAccountSettings() {
                const response = yield axiosBoa.get(brandsOpenApi.accounts.getCurrent, {
                    params: { select: 'settings,attribution_window_settings' }
                });
                self.currentAccount.settings = response.settings;
                self.currentAccount.attributionWindowSettings = response.attributionWindowSettings;
            }),
            getTrackingSettings: action(function* getTrackingSettings() {
                const account = yield axiosBoa.get(brandsOpenApi.accounts.getCurrent, {
                    params: { select: 'shopify_cross_check_guide,ga4_settings,utm_settings,triple_whale_settings' }
                });
                self.currentAccount.shopifyCrossCheckGuide = account.shopifyCrossCheckGuide ?? false;
                self.currentAccount.ga4Settings = account.ga4Settings;
                self.currentAccount.utmSettings = account.utmSettings;
                self.currentAccount.tripleWhaleSettings = account.tripleWhaleSettings;
            }),
            updateTrackingSettings: action(function* updateTrackingSettings(settings) {
                const updatedAccount = yield axiosBoa.patch(brandsOpenApi.accounts.update, settings);
                self.currentAccount.ga4Settings = updatedAccount.ga4Settings;
                self.currentAccount.utmSettings = updatedAccount.utmSettings;
                self.currentAccount.tripleWhaleSettings = updatedAccount.tripleWhaleSettings;
            }),
            updateTripleWhaleInvite: action(
                function* updateTripleWhaleInvite(inviteSent) {
                    const updatedAccount = yield axiosBoa.patch(brandsOpenApi.accounts.update, {
                        tripleWhaleSettings: inviteSent ? { enabled: true, inviteSent } : { inviteSent }
                    });
                    self.currentAccount.tripleWhaleSettings = updatedAccount.tripleWhaleSettings;
                },
                // the tab shows its own message, so skip the global error banner
                { onError: (error) => ({ error }) }
            ),
            updateCommissionOffset: action(function* updateCommissionOffset(commissionOffset) {
                const updatedAccount = yield axiosBoa.patch(brandsOpenApi.accounts.update, commissionOffset);
                self.currentAccount.commission.commissionOffsetEnabled =
                    updatedAccount.commission.commissionOffsetEnabled;
            }),
            //  * Setup info
            getSetupInfo: action(function* getSetupInfo() {
                const response = yield axiosBoa.get(brandsOpenApi.setup.info);
                response.paymentMethod = mapPaymentMethod(response.paymentMethod);
                self.setupInfo = response;
                self.historicalSetupOrdersPending = isHistoricalSetupPending({
                    pending: self.historicalSetupOrdersPending,
                    connected: response.historical?.hasOrders,
                    startedAt: self.historicalSetupStartedAt
                });
                self.historicalSetupCustomersPending = isHistoricalSetupPending({
                    pending: self.historicalSetupCustomersPending,
                    connected: response.historical?.hasCustomers,
                    startedAt: self.historicalSetupStartedAt
                });
            }),
            // * States
            getStates: action(function* getStates() {
                const response = yield axiosBoa.get(brandsOpenApi.taxSettings.get);
                self.states = response.data;
            }),
            // * Team Members
            handleTeamMemberCreate: action(
                function* handleTeamMemberCreate(member, onCancel) {
                    yield axiosBoa.post(brandsOpenApi.teamMembers.create, member);
                    yield self.getTeamMembers();
                    onCancel();
                },
                {
                    getSuccessMessage: (member) => {
                        return { title: `Invite to ${member.email} was sent`, description: '' };
                    }
                }
            ),
            handleTeamMemberDelete: action(
                function* handleTeamMemberDelete(member) {
                    yield axiosBoa.delete(brandsOpenApi.teamMembers.delete(member.id));
                    self.teamMembers = self.teamMembers.filter((item) => item.id !== member.id);
                },
                { getSuccessMessage: () => ({ title: 'Team member successfully deleted', description: '' }) }
            ),
            handleTeamMemberUpdate: action(
                function* handleTeamMemberUpdate(member) {
                    const updatedMember = yield axiosBoa.patch(brandsOpenApi.teamMembers.update(member.id), member);
                    self.teamMembers = self.teamMembers.map((item) =>
                        item.id === updatedMember.id
                            ? {
                                  ...item,
                                  role: updatedMember.role,
                                  user: {
                                      ...item.user,
                                      ...updatedMember.user
                                  }
                              }
                            : item
                    );
                },
                {
                    getSuccessMessage: () => ({
                        title: 'The member has been successfully updated',
                        description: ''
                    })
                }
            ),
            handleTeamMemberResendInvite: action(
                function* handleTeamMemberResendInvite(member) {
                    yield axiosBoa.post(brandsOpenApi.teamMembers.resendInvite(member.id));
                },
                {
                    getSuccessMessage: (member) => ({
                        title: `Invite to ${member.user.email} was sent`,
                        description: ''
                    })
                }
            ),
            getTeamMembers: action(function* getTeamMembers() {
                const response = yield axiosBoa.get(brandsOpenApi.teamMembers.list);
                self.teamMembers = response?.data;
            }),
            handleTransferOwnership: action(function* handleTransferOwnership(member, setSuccess) {
                const updatedMember = yield axiosBoa.post(brandsOpenApi.ownerShip.transfer, member);
                self.teamMembers = self.teamMembers.map((item) =>
                    item.id === updatedMember.id ? { ...item, status: updatedMember.status } : item
                );

                setSuccess(true);
            }),
            handleCancelOwnership: action(function* handleCancelOwnership(member) {
                const updatedMember = yield axiosBoa.patch(brandsOpenApi.ownerShip.cancel(member.id));
                self.teamMembers = self.teamMembers.map((item) =>
                    item.id === updatedMember.id ? { ...item, status: updatedMember.status } : item
                );
            }),
            handleConfirmOwnership: action(
                function* handleConfirmOwnership(token) {
                    yield axiosBoa.post(brandsOpenApi.teamMembers.ownershipConfirm, { token });
                },
                {
                    getSuccessMessage: () => ({
                        title: 'The ownership has been successfully transferred',
                        description: ''
                    })
                }
            ),
            // * Handle customers info form save (launch check)
            customerInfoFormUpdate: action(function* customerInfoFormUpdate(values) {
                const [updatedEmail, updatedPhone, updatedAddress] = yield Promise.all([
                    axiosBoa.patch(brandsOpenApi.contacts.update(self.customersEmail.id), {
                        type: CONTACT_TYPE.FOR_CUSTOMER,
                        confirmed: true,
                        valueType: CONTACT_VALUE_TYPE.EMAIL,
                        value: values.email
                    }),
                    axiosBoa.patch(brandsOpenApi.contacts.update(self.customersPhone.id), {
                        type: CONTACT_TYPE.FOR_CUSTOMER,
                        confirmed: true,
                        valueType: CONTACT_VALUE_TYPE.PHONE,
                        value: values.phone ? `+${values.phone}` : values.phone
                    }),
                    axiosBoa.patch(brandsOpenApi.addresses.update(self.addressForCustomers.id), {
                        country: 'US',
                        province: values.province,
                        city: values.city,
                        address1: values.address1,
                        address2: values.address2,
                        zipCode: values.zipCode?.toString() || '',
                        confirmed: true
                    })
                ]);
                Object.assign(self.customersEmail, updatedEmail);
                Object.assign(self.customersPhone, updatedPhone);
                Object.assign(self.addressForCustomers, updatedAddress);
            }),
            klicklyInfoFormUpdate: action(function* klicklyInfoFormUpdate(values) {
                const [updatedEmail, updatedPhone, updatedAddress] = yield Promise.all([
                    axiosBoa.patch(brandsOpenApi.contacts.update(self.klicklyEmail.id), {
                        type: CONTACT_TYPE.FOR_KLICKLY,
                        confirmed: true,
                        valueType: CONTACT_VALUE_TYPE.EMAIL,
                        value: values.email
                    }),
                    axiosBoa.patch(brandsOpenApi.contacts.update(self.klicklyPhone.id), {
                        type: CONTACT_TYPE.FOR_KLICKLY,
                        confirmed: true,
                        valueType: CONTACT_VALUE_TYPE.PHONE,
                        value: values.phone ? `+${values.phone}` : values.phone
                    }),
                    axiosBoa.patch(brandsOpenApi.addresses.update(self.addressForKlickly.id), {
                        country: 'US',
                        province: values.province,
                        city: values.city,
                        address1: values.address1,
                        address2: values.address2,
                        zipCode: values.zipCode?.toString() || '',
                        confirmed: true
                    })
                ]);
                Object.assign(self.klicklyEmail, updatedEmail);
                Object.assign(self.klicklyPhone, updatedPhone);
                Object.assign(self.addressForKlickly, updatedAddress);
            }),
            // * Handle settings pages save
            companyInfoUpdate: action(function* companyInfoUpdate(values) {
                const [{ companyName, settings }, updatedEmail, updatedPhone, updatedAddress] = yield Promise.all([
                    axiosBoa.patch(brandsOpenApi.accounts.update, {
                        companyName: values.companyName,
                        timeZoneDashboard: values.timeZoneDashboard
                    }),
                    axiosBoa.patch(brandsOpenApi.contacts.update(self.klicklyEmail.id), {
                        type: CONTACT_TYPE.FOR_KLICKLY,
                        confirmed: true,
                        valueType: CONTACT_VALUE_TYPE.EMAIL,
                        value: values.email
                    }),
                    axiosBoa.patch(brandsOpenApi.contacts.update(self.klicklyPhone.id), {
                        type: CONTACT_TYPE.FOR_KLICKLY,
                        confirmed: true,
                        valueType: CONTACT_VALUE_TYPE.PHONE,
                        value: `+${values.phone}`
                    }),
                    axiosBoa.patch(brandsOpenApi.addresses.update(self.addressForKlickly.id), {
                        country: 'US',
                        province: values.province,
                        city: values.city,
                        address1: values.address1,
                        address2: values.address2,
                        zipCode: values.zipCode?.toString() || '',
                        confirmed: true
                    })
                ]);
                self.root.authStore.updateAccount(self.currentAccount.id, { companyName });
                self.currentAccount.companyName = companyName;
                self.currentAccount.settings.timeZoneDashboard = settings.timeZoneDashboard;
                Object.assign(self.klicklyEmail, updatedEmail);
                Object.assign(self.klicklyPhone, updatedPhone);
                Object.assign(self.addressForKlickly, updatedAddress);
            }),
            handleAccountManagementSave: action(function* handleAccountManagementSave({
                coldCustomer,
                newCustomer,
                restOfSite
            }) {
                yield Promise.allSettled([self.updateCommissions({ coldCustomer, newCustomer, restOfSite })]);
            }),
            customerManagementUpdate: action(function* customerManagementUpdate(values) {
                const promises = [];
                const isChanged = JSON.stringify(self.states) !== JSON.stringify(values.statesList);
                if (isChanged) {
                    const updateTaxSettings = axiosBoa.post(brandsOpenApi.taxSettings.set, {
                        states: values.statesList
                    });
                    promises.push(updateTaxSettings);
                }
                const [updatedEmail, updatedPhone, updatedAddress, updatedAccount] = yield Promise.all([
                    axiosBoa.patch(brandsOpenApi.contacts.update(self.customersEmail.id), {
                        type: CONTACT_TYPE.FOR_CUSTOMER,
                        confirmed: true,
                        valueType: CONTACT_VALUE_TYPE.EMAIL,
                        value: values.email
                    }),
                    axiosBoa.patch(brandsOpenApi.contacts.update(self.customersPhone.id), {
                        type: CONTACT_TYPE.FOR_CUSTOMER,
                        confirmed: true,
                        valueType: CONTACT_VALUE_TYPE.PHONE,
                        value: values.phone ? `+${values.phone}` : values.phone
                    }),
                    axiosBoa.patch(brandsOpenApi.addresses.update(self.addressForCustomers.id), {
                        country: 'US',
                        province: values.province,
                        city: values.city,
                        address1: values.address1,
                        address2: values.address2,
                        zipCode: values.zipCode?.toString() || '',
                        confirmed: true
                    }),
                    axiosBoa.patch(brandsOpenApi.accounts.update, {
                        packageSize: {
                            length: values.length,
                            width: values.width,
                            height: values.height,
                            weight: values.weight
                        }
                    }),
                    ...promises
                ]);
                Object.assign(self.customersEmail, updatedEmail);
                Object.assign(self.customersPhone, updatedPhone);
                Object.assign(self.addressForCustomers, updatedAddress);
                Object.assign(self.currentAccount.settings.packageSize, updatedAccount.settings.packageSize);
                self.states = values.statesList;
            }),
            handleNotificationContactsSave: action(function* handleNotificationContactsSave({
                contactsToCreate,
                contactsToUpdate,
                contactsToDelete
            }) {
                yield Promise.allSettled([
                    ...contactsToCreate.map((contact) =>
                        axiosBoa.post(brandsOpenApi.contacts.create, {
                            ...contact,
                            type: CONTACT_TYPE.NOTIFICATION,
                            value: contact.valueType === CONTACT_VALUE_TYPE.PHONE ? `+${contact.value}` : contact.value
                        })
                    ),
                    ...contactsToUpdate.map((contact) =>
                        axiosBoa.patch(brandsOpenApi.contacts.update(contact.id), {
                            ...contact,
                            type:
                                contact.id === self.klicklyEmail.id
                                    ? CONTACT_TYPE.FOR_KLICKLY
                                    : CONTACT_TYPE.NOTIFICATION,
                            value: contact.valueType === CONTACT_VALUE_TYPE.PHONE ? `+${contact.value}` : contact.value
                        })
                    ),
                    ...contactsToDelete.map((contact) => axiosBoa.delete(brandsOpenApi.contacts.delete(contact.id)))
                ]);
                yield self.getNotificationContacts();
                if (contactsToUpdate.find((contact) => contact.id === self.klicklyEmail.id)) {
                    yield self.getContactsForKlickly();
                }
            }),
            // * Stripe connected account
            handleCreateStripeAccount: action(function* handleCreateStripeAccount() {
                const connectOnboardingLink = yield axiosBoa.post(brandsOpenApi.accounts.createStripeAccount);
                window.location.href = connectOnboardingLink.url;
            }),
            handleUpdateStripeAccount: action(function* handleUpdateStripeAccount(type) {
                const connectOnboardingLink = yield axiosBoa.get(brandsOpenApi.accounts.updateStripeAccount(type));
                window.location.href = connectOnboardingLink.url;
            }),
            handleConnectToStripeAccount() {
                const params = new URLSearchParams({
                    response_type: 'code',
                    client_id: config.stripeClientId,
                    scope: 'read_write',
                    redirect_uri: `${window.location.origin}/profile/account-settings/payments-settings`
                });
                const link = `https://connect.stripe.com/oauth/authorize?${params.toString()}`;
                window.location.href = link;
            },
            stripeAccountOauthCallback: action(function* stripeAccountOauthCallback(code) {
                const { status } = yield axiosBoa.post(brandsOpenApi.accounts.stripeOauthCallback, { code });
                self.currentAccount.stripeSettings.stripeAccountStatus = status;
            }),
            stripeAccountInfo: action(
                function* stripeAccountInfo() {
                    self.accountStatusPending = true;

                    const { status } = yield axiosBoa.get(brandsOpenApi.accounts.stripeAccountInfo);
                    self.currentAccount.stripeSettings.stripeAccountStatus = status;
                },
                { onFinally: () => (self.accountStatusPending = false) }
            ),
            //  * Others
            setupScriptTag: action(
                function* setupScriptTag() {
                    self.scriptTagSetupPending = true;

                    const response = yield axiosBoa.post(brandsOpenApi.setup.processor, { type: 'script-tag' });
                    self.currentAccount.domainScripts.forEach(
                        (domainScript) => (domainScript.status = SCRIPT_TAG_STATUS.PENDING)
                    );
                    self.setupInfo.scriptTag.status = response.status;
                },
                { onFinally: () => (self.scriptTagSetupPending = false) }
            ),
            setupPurchase: action(
                function* setupPurchase() {
                    self.purchaseSetupPending = true;
                    const response = yield axiosBoa.post(brandsOpenApi.setup.processor, { type: 'purchase' });
                    self.setupInfo.purchaseEventStatus = response.status;
                },
                { onFinally: () => (self.purchaseSetupPending = false) }
            ),
            setupHistorical: action(function* setupHistorical(type) {
                if (type === 'orders') {
                    self.historicalSetupOrdersPending = true;
                } else {
                    self.historicalSetupCustomersPending = true;
                }
                self.historicalSetupStartedAt = Date.now();

                try {
                    yield axiosBoa.post(brandsOpenApi.setup.processor, { type: 'historical' });
                } catch (error) {
                    self.historicalSetupOrdersPending = false;
                    self.historicalSetupCustomersPending = false;
                    throw error;
                }
            }),
            getDomainScripts: action(function* getDomainScripts() {
                const account = yield axiosBoa.get(brandsOpenApi.accounts.getCurrent, {
                    params: { select: 'domain_scripts' }
                });
                Object.assign(self.currentAccount, account);
            }),
            paymentIntent: action(function* paymentIntent() {
                const response = yield axiosBoa.post(brandsOpenApi.setup.paymentIntent.get);
                return response;
            }),
            paymentMethodUpdate: action(function* paymentMethodUpdate(data = {}) {
                const response = yield axiosBoa.post(brandsOpenApi.accounts.paymentMethodUpdate, data);
                self.setupInfo.paymentMethod = mapPaymentMethod(response.paymentMethod);
            }),
            verifyToken: action(function* verifyToken(token) {
                self.verification = yield axiosBoa.get(brandsOpenApi.teamMembers.verifyToken(token));
            }),
            onInviteComplete: action(
                function* onInviteComplete(action, token, values) {
                    self.inviteCompletePending = true;
                    yield axiosBoa.post(brandsOpenApi.teamMembers.userComplete, { action, token, ...values });
                },
                { onFinally: () => (self.inviteCompletePending = false) }
            ),
            setGUIDRequestMessage(message) {
                if (self.currentAccount?.thirdPartyAppsSettings) {
                    self.currentAccount.thirdPartyAppsSettings.transUnionRequestMessage = message;
                }
            },
            submitFeedback: action(function* submitFeedback(type, reason, details) {
                const feedbackReason = details ? `${reason} - ${details}` : reason;
                yield axiosBoa.post(brandsOpenApi.feedback.submit, {
                    type,
                    reason: feedbackReason
                });
            })
        };
    });
